import { useState, useRef, useEffect, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

export interface DropdownItem {
  label: string;
  value: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface DropdownProps {
  trigger?: ReactNode;
  items: DropdownItem[];
  onSelect: (item: DropdownItem) => void;
  placeholder?: string;
  value?: string;
  className?: string;
}

const Dropdown = ({
  trigger,
  items,
  onSelect,
  placeholder = 'Select an option',
  value,
  className = '',
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedItem = items.find((item) => item.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item: DropdownItem) => {
    if (!item.disabled) {
      onSelect(item);
      setIsOpen(false);
    }
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger */}
      {trigger ? (
        <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`
            w-full flex items-center justify-between px-4 py-3 rounded-2xl
            border border-gray-200 bg-white text-left
            hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-lime-400
            transition-all duration-200
            dark:bg-dark-700 dark:border-dark-600 dark:hover:border-dark-500
          `}
        >
          <span className={selectedItem ? 'text-dark-800 dark:text-white' : 'text-gray-400 dark:text-gray-500'}>
            {selectedItem ? selectedItem.label : placeholder}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-2xl shadow-card border border-gray-100 py-2 overflow-hidden dark:bg-dark-800 dark:border-dark-700">
          {items.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => handleSelect(item)}
              disabled={item.disabled}
              className={`
                w-full flex items-center gap-3 px-4 py-3 text-left
                transition-colors duration-150
                ${item.disabled
                  ? 'text-gray-300 cursor-not-allowed dark:text-gray-600'
                  : item.value === value
                    ? 'bg-lime-50 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400'
                    : 'text-dark-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-dark-700'
                }
              `}
            >
              {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
